import { createSelector } from '@reduxjs/toolkit';
import processTree from 'scenes/ProjectBank/components/processTree';
import {
  createProjectBankFormsRootFolder,
  isProjectTypeProjectBankFolder,
} from 'scenes/ProjectBank/utils/projectBankForms';
import { selectProjectUsers } from 'store/projectUsers/selectors/projectUsers.selectors';
import {
  selectProjects,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';

const selectProjectBankState = (state) => state.projectBank;

export const selectIsProjectTreeLoading = createSelector(
  [selectProjectBankState],
  (state) => state.projectTreeLoading
);

const selectProjectBankBaseFilesFilePermissions = createSelector(
  [selectProjectBankState],
  (state) => state.filesFilePermissions.entities
);

const selectProjectBankFormsVirtualFilePermissions = createSelector(
  [selectProjectBankState],
  (state) => state.projectBankFormsFiles.entities
);

export const selectProjectBankFilesFilePermissions = createSelector(
  [
    selectProjectBankBaseFilesFilePermissions,
    selectProjectBankFormsVirtualFilePermissions,
  ],
  (baseFiles, virtualFiles) => ({
    ...baseFiles,
    ...virtualFiles,
  })
);

export const selectSelectedProjectBankFolderPath = createSelector(
  [selectProjectBankState],
  (state) => state.selectedFolderPath
);

export const selectSelectedProjectBankFolderId = createSelector(
  [selectProjectBankState],
  (state) => state.selectedFolderId
);

export const selectProjectBankSortBy = createSelector(
  [selectProjectBankState],
  (state) => state.sortBy
);

export const selectSelectedTilaajavastuuBusinessId = createSelector(
  [selectProjectBankState],
  (state) => state.selectedTilaajavastuuBusinessId
);

export const selectIsLoadingTilaajavastuuDocuments = createSelector(
  [selectProjectBankState],
  (state) => state.isLoadingTilaajavastuuDocuments
);
export const selectSelectedPreviewTilaajavastuuDocument = createSelector(
  [selectProjectBankState],
  (state) => state.selectedPreviewTilaajavastuuDocument
);

export const selectTilaajavastuuDocumentEntities = createSelector(
  [selectProjectBankState],
  (state) => state.tilaajavastuuDocuments.entities
);

export const selectSelectedTilaajavastuuFolderProjectId = createSelector(
  [selectProjectBankState],
  (state) => state.selectedTilaajavastuuFolderProjectId
);

export const selectOpenedProjectTreeFolderIds = createSelector(
  [selectProjectBankState],
  (state) => state.openedProjectTreeFolderIds
);
export const selectOpenedPrivateProjectBankTreeFolderIds = createSelector(
  [selectProjectBankState],
  (state) => state.openedPrivateProjectBankTreeFolderIds
);

export const selectIsPdfPreviewDialogOpen = createSelector(
  [selectProjectBankState],
  (state) => state.isPdfPreviewDialogOpen
);

export const selectProjectBankViewMode = createSelector(
  [selectProjectBankState],
  (state) => state.projectBankViewMode
);

export const selectSelectedProjectBankFiles = createSelector(
  [selectProjectBankState],
  (state) => state.selectedProjectBankFiles
);

export const selectIsProjectBankFileSelectDialogOpen = createSelector(
  [selectProjectBankState],
  (state) => state.isProjectBankFileSelectDialogOpen
);

export const selectIsTransferFolderStructureDialogOpen = createSelector(
  [selectProjectBankState],
  (state) => state.isTransferFolderStructureDialogOpen
);

export const selectIsAddFolderDialogOpen = createSelector(
  [selectProjectBankState],
  (state) => state.isAddFolderDialogOpen
);

export const selectIsAddingFolder = createSelector(
  [selectProjectBankState],
  (state) => state.isAddingFolder
);

export const selectTilaajavastuuIntegrationStatusesByBusinessId =
  createSelector(
    [selectProjectBankState],
    (state) => state.tilaajavastuuIntegrationStatusesByBusinessId
  );

export const selectTilaajavastuuDocumentsArray = createSelector(
  [selectTilaajavastuuDocumentEntities],
  (entities) => Object.values(entities)
);

export const selectIsLoadingFolderStructureTransfer = createSelector(
  [selectProjectBankState],
  (state) => state.isLoadingFolderStructureTransfer
);

export const selectSelectedProjectBankVisibilityMode = createSelector(
  [selectProjectBankState],
  (state) => state.selectedProjectBankVisibilityMode
);

export const selectSelectedProjectBankDataTransferTargetVisibilityMode =
  createSelector(
    [selectProjectBankState],
    (state) => state.selectedProjectBankDataTransferTargetVisibilityMode
  );

export const selectFoundTilaajavastuuDocumentsArray = createSelector(
  [selectTilaajavastuuDocumentsArray],
  (tilaajavastuuDocuments) =>
    tilaajavastuuDocuments.filter((document) => document.status !== 'NOT_FOUND')
);

export const selectInsufficientTilaajavastuuDocumentsBusinessIdsArrayByProjectId =
  createSelector(
    [selectFoundTilaajavastuuDocumentsArray, (_, args) => args],
    (tilaajavastuuDocuments, args) => {
      const notAlarmedStatuses = ['Ok', 'Tietoja odotetaan'];
      return tilaajavastuuDocuments
        .filter(
          (document) =>
            !notAlarmedStatuses.includes(document.status) &&
            document.projectId === args &&
            !document.deletedAt
        )
        .map((document) => document.businessId.split('#')[0]);
    }
  );

export const selectProjectBankFilesFilePermissionsArray = createSelector(
  [selectProjectBankFilesFilePermissions],
  (filePermissions) =>
    Object.values(filePermissions).filter(
      (filePermission) => !filePermission.deletedAt
    )
);

const selectProjectFolderFilePermissionsBase = createSelector(
  [selectProjectBankState],
  (state) => state.projectFolderFilePermissions.entities
);

const selectProjectFolderFilePermissionsBaseArray = createSelector(
  [selectProjectFolderFilePermissionsBase],
  (filePermissions) =>
    Object.values(filePermissions).filter(
      (filePermission) => !filePermission.deletedAt
    )
);

const selectProjectBankFormsRootFolders = createSelector(
  [selectProjectFolderFilePermissionsBaseArray, selectProjects],
  (folderFilePermissions, projects) =>
    folderFilePermissions
      .filter((folder) => {
        if (!isProjectTypeProjectBankFolder(folder)) return false;
        const projectId =
          typeof folder.project === 'object' ? folder.project?.id : folder.project;
        const project = projectId ? projects[projectId] : null;
        if (
          project &&
          project.businessTypeEnum === 'EMPLOYER' &&
          !project.masterParent
        ) {
          return false;
        }
        return true;
      })
      .map((folder) => createProjectBankFormsRootFolder(folder))
      .filter(Boolean)
);

const selectProjectBankFormsVirtualFolderPermissions = createSelector(
  [selectProjectBankState],
  (state) => state.projectBankFormsFolders.entities
);

const selectProjectBankFormsVirtualFolderPermissionsArray = createSelector(
  [selectProjectBankFormsVirtualFolderPermissions],
  (filePermissions) =>
    Object.values(filePermissions).filter(
      (filePermission) => !filePermission.deletedAt
    )
);

export const selectProjectFolderFilePermissionsArray = createSelector(
  [
    selectProjectFolderFilePermissionsBaseArray,
    selectProjectBankFormsRootFolders,
    selectProjectBankFormsVirtualFolderPermissionsArray,
  ],
  (baseFolders, formsRootFolders, virtualFolders) => [
    ...baseFolders,
    ...formsRootFolders,
    ...virtualFolders,
  ]
);

export const selectProjectFolderFilePermissions = createSelector(
  [selectProjectFolderFilePermissionsArray],
  (filePermissions) =>
    filePermissions.reduce((acc, filePermission) => {
      acc[filePermission.id] = filePermission;
      return acc;
    }, {})
);

export const selectSelectedProjectBankFolder = createSelector(
  [selectProjectFolderFilePermissions, selectSelectedProjectBankFolderId],
  (folders, selectedFolderId) => folders[selectedFolderId]
);

export const selectSelectedPrivateProjectBankFolderId = createSelector(
  [selectProjectBankState],
  (state) => state.selectedPrivateProjectBankFolderId
);

export const selectPrivateProjectBankItemsEntities = createSelector(
  [selectProjectBankState],
  (state) => state.privateProjectBankItems.entities
);

export const selectSelectedPrivateProjectBankFolderItem = createSelector(
  [
    selectPrivateProjectBankItemsEntities,
    selectSelectedPrivateProjectBankFolderId,
  ],
  (privateProjectBankEntities, selectedFolderId) =>
    privateProjectBankEntities
      ? privateProjectBankEntities[selectedFolderId]
      : undefined
);

export const selectPrivateProjectBankItemsArray = createSelector(
  [selectPrivateProjectBankItemsEntities],
  (entities) => Object.values(entities)
);

export const selectSelectedProjectPrivateProjectBankItems = createSelector(
  [selectPrivateProjectBankItemsArray, selectSelectedProjectId],
  (projectBankItems, selectedProjectId) =>
    projectBankItems.filter(
      (item) => item.projectId === selectedProjectId && !item.deletedAt
    )
);

export const selectSelectedProjectPrivateProjectBankFolders = createSelector(
  [selectSelectedProjectPrivateProjectBankItems],
  (items) => items.filter((item) => item.type === 'FOLDER')
);

export const selectSelectedProjectPrivateProjectBankFiles = createSelector(
  [selectSelectedProjectPrivateProjectBankItems],
  (items) => items.filter((item) => item.type === 'FILE')
);

export const selectProjectFolderFilePermissionsArrayWithJoinedData =
  createSelector(
    [
      selectProjectFolderFilePermissionsArray,
      selectProjects,
      selectProjectUsers,
    ],
    (filePermissions, projects, users) =>
      filePermissions.map((filePermission) => ({
        ...filePermission,
        project:
          typeof filePermission.project === 'object'
            ? filePermission.project
            : projects[filePermission.project]
              ? projects[filePermission.project]
              : { id: filePermission.project },
        user:
          typeof filePermission.user === 'object'
            ? filePermission.user
            : users[filePermission.user]
              ? users[filePermission.user]
              : { id: filePermission.user, firstName: '', lastName: '' },
      }))
  );

export const selectProjectBankFilesFilePermissionsArrayWithJoinedData =
  createSelector(
    [
      selectProjectBankFilesFilePermissionsArray,
      selectProjectUsers,
      selectProjects,
    ],
    (files, users, projects) =>
      files.map((file) => ({
        ...file,
        user:
          typeof file.user === 'object'
            ? file.user
            : users[file.user]
              ? users[file.user]
              : { id: file.user, firstName: '', lastName: '' },
        project:
          typeof file.project === 'object'
            ? file.project
            : projects[file.project]
              ? projects[file.project]
              : { id: file.project },
      }))
  );

export const selectSelectedProjectBankFolderContentFiles = createSelector(
  [
    selectSelectedProjectBankFolder,
    selectProjectBankFilesFilePermissionsArrayWithJoinedData,
  ],
  (selectedFolder, files) => {
    if (selectedFolder) {
      if (selectedFolder.name === 'images') {
        // handle showing images from possible duplicate images folders in project

        const filesInImagesFoldersOfProject = files.filter((file) => {
          const fileInImagesFolder =
            file && file.path && file.path.includes('images') ? true : false;
          return (
            file.parent === selectedFolder.id ||
            (fileInImagesFolder && file.project.id === selectedFolder.project)
          );
        });
        return filesInImagesFoldersOfProject;
      } else {
        return files.filter((file) => file.parent === selectedFolder.id);
      }
    } else {
      return [];
    }
  }
);

export const selectSelectedPrivateProjectBankFolderContentFiles =
  createSelector(
    [
      selectSelectedPrivateProjectBankFolderId,
      selectSelectedProjectPrivateProjectBankFiles,
    ],
    (selectedPrivateFolderId, privateProjectBankFiles) =>
      selectedPrivateFolderId
        ? privateProjectBankFiles.filter(
          (file) => file.parentFolderId === selectedPrivateFolderId
        )
        : privateProjectBankFiles.filter((file) => !file.parentFolderId)
  );

export const selectSelectedPrivateProjectBankFolderContentFolders =
  createSelector(
    [
      selectSelectedPrivateProjectBankFolderId,
      selectSelectedProjectPrivateProjectBankFolders,
    ],
    (selectedPrivateFolderId, privateProjectBankFolders) =>
      selectedPrivateFolderId
        ? privateProjectBankFolders.filter(
          (folder) => folder.parentFolderId === selectedPrivateFolderId
        )
        : privateProjectBankFolders.filter((folder) => !folder.parentFolderId)
  );

export const selectSelectedProjectBankFolderChildFolders = createSelector(
  [
    selectProjectFolderFilePermissionsArrayWithJoinedData,
    selectSelectedProjectBankFolderId,
    selectSelectedProjectBankFolderPath,
  ],
  (folderFilePermissions, selectedFolderId, selectedFolderPath) =>
    folderFilePermissions.filter(
      (folder) =>
        folder.parent === selectedFolderId ||
        folder.parentPath === selectedFolderPath
    )
);

export const selectSelectedProjectBankFolderChildProjectFolders =
  createSelector([selectSelectedProjectBankFolderChildFolders], (folders) => {
    const childProjects = folders.filter(
      (folder) =>
        (folder.businessId && folder.key.split('/').length === 3) ||
        (!folder.businessId && folder.key.split('/').length === 2)
    );

    return Object.values(processTree(childProjects));
  });

export const selectSelectedProjectBankFolderChildNormalFolders = createSelector(
  [selectSelectedProjectBankFolderChildFolders],
  (folders) =>
    folders.filter(
      (folder) =>
        (folder.businessId && folder.key.split('/').length > 3) ||
        (!folder.businessId && folder.key.split('/').length > 2)
    )
);

export const selectIsFolderFilesLoading = createSelector(
  [selectProjectBankState],
  (state) => state.isFolderFilesLoading
);

export const selectAllCurrentProjectBankFolderContentItems = createSelector(
  [
    selectSelectedProjectBankFolderContentFiles,
    selectSelectedProjectBankFolderChildProjectFolders,
    selectSelectedProjectBankFolderChildNormalFolders,
    selectProjectBankSortBy,
  ],
  (files, projects, folders, sortBy) => {
    const allItems = [...folders, ...projects, ...files]; // Folders and projects before fiels

    let sorted = [];

    switch (sortBy) {
      case 'name-ascending':
        sorted = [...allItems];
        sorted = sorted.sort((a, b) =>
          checkName(a) < checkName(b) ? -1 : checkName(a) > checkName(b) ? 1 : 0
        );
        return sorted;
      case 'name-descending':
        sorted = [...allItems];
        sorted = sorted.sort((a, b) =>
          checkName(a) > checkName(b) ? -1 : checkName(a) < checkName(b) ? 1 : 0
        );
        return sorted;
      case 'date-ascending':
        sorted = [...allItems];
        sorted = sorted.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        return sorted;
      case 'date-descending':
        sorted = [...allItems];
        sorted = sorted.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        return sorted;
      case 'creator-ascending':
        sorted = [...allItems];
        sorted = sorted.sort((a, b) =>
          a.user.firstName.toLowerCase() < b.user.firstName.toLowerCase()
            ? -1
            : a.user.firstName.toLowerCase() > b.user.firstName.toLowerCase()
              ? 1
              : 0
        );
        return sorted;
      case 'creator-descending':
        sorted = [...allItems];
        sorted = sorted.sort((a, b) =>
          a.user.firstName.toLowerCase() > b.user.firstName.toLowerCase()
            ? -1
            : a.user.firstName.toLowerCase() < b.user.firstName.toLowerCase()
              ? 1
              : 0
        );
        return sorted;
      default: {
        const dateSortDesc = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
        sorted = [
          ...([...folders].sort(dateSortDesc)),
          ...([...projects].sort(dateSortDesc)),
          ...([...files].sort(dateSortDesc)),
        ];
      }
    }

    return sorted;
  }
);

export const selectAllCurrentPrivateProjectBankFolderContentItems =
  createSelector(
    [
      selectSelectedPrivateProjectBankFolderContentFiles,
      selectSelectedPrivateProjectBankFolderContentFolders,
      selectProjectBankSortBy,
    ],
    (files, folders, sortBy) => {
      const allItems = [...folders, ...files]; // Folders and projects before fiels

      let sorted = [];

      switch (sortBy) {
        case 'name-ascending':
          sorted = [...allItems];
          sorted = sorted.sort((a, b) =>
            checkName(a) < checkName(b)
              ? -1
              : checkName(a) > checkName(b)
                ? 1
                : 0
          );
          return sorted;
        case 'name-descending':
          sorted = [...allItems];
          sorted = sorted.sort((a, b) =>
            checkName(a) > checkName(b)
              ? -1
              : checkName(a) < checkName(b)
                ? 1
                : 0
          );
          return sorted;
        case 'date-ascending':
          sorted = [...allItems];
          sorted = sorted.sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );
          return sorted;
        case 'date-descending':
          sorted = [...allItems];
          sorted = sorted.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          return sorted;
        case 'creator-ascending':
          sorted = [...allItems];
          sorted = sorted.sort((a, b) =>
            a.user.firstName.toLowerCase() < b.user.firstName.toLowerCase()
              ? -1
              : a.user.firstName.toLowerCase() > b.user.firstName.toLowerCase()
                ? 1
                : 0
          );
          return sorted;
        case 'creator-descending':
          sorted = [...allItems];
          sorted = sorted.sort((a, b) =>
            a.user.firstName.toLowerCase() > b.user.firstName.toLowerCase()
              ? -1
              : a.user.firstName.toLowerCase() < b.user.firstName.toLowerCase()
                ? 1
                : 0
          );
          return sorted;
        default:
          sorted = [...allItems];
      }

      return sorted;
    }
  );

const checkName = (filePermission) => {
  let name;
  if (!filePermission.isRoot) {
    name = filePermission.name.toLowerCase();
  } else {
    name = filePermission.project.name.toLowerCase();
  }
  return name;
};

export const selectInitialProjectBankPath = createSelector(
  [selectProjectBankState],
  (state) => state.initialProjectBankPath
);

export const selectProjectBankProjectTree = createSelector(
  [
    selectProjectFolderFilePermissionsArrayWithJoinedData,
    selectInitialProjectBankPath,
  ],
  (folderFilePermissions, urlPath) => {
    const filteredProjects = folderFilePermissions.filter((folder) =>
      folder.path?.startsWith(urlPath)
    );
    const tree = processTree(filteredProjects, urlPath);

    return tree;
  }
);

export const selectFolderTree = createSelector(
  [selectProjectBankProjectTree, selectInitialProjectBankPath],
  (projectTree, urlPath) => {
    const treeFolders = Object.values(projectTree);

    const rootFolder = treeFolders.find((folder) => folder.path === urlPath);

    return rootFolder;
  }
);

export const selectTransferFolderStructureTargetProjectId = createSelector(
  [selectProjectBankState],
  (state) => state.transferFolderStructureTargetProjectId
);

export const selectTransferFolderStructureTargetProject = createSelector(
  [selectProjects, selectTransferFolderStructureTargetProjectId],
  (projects, targetProjectId) => projects[targetProjectId]
);

const getDescendantFolders = (folderId, allFolders) => {
  const directChildren = allFolders.filter(
    (folder) => folder.parent === folderId
  );

  let descendants = [...directChildren];

  for (const child of directChildren) {
    const childDescendants = getDescendantFolders(child.id, allFolders);
    descendants = [...descendants, ...childDescendants];
  }

  return descendants;
};

const buildFolderTreeWithChildren = (folderId, allFolders, allFiles) => {
  const folder = allFolders.find((f) => f.id === folderId);

  if (!folder) {
    return null;
  }

  // Get direct child folders
  const childFolders = allFolders.filter((f) => f.parent === folderId);

  // Get direct child files
  const childFiles = allFiles.filter((f) => f.parent === folderId);

  // Recursively build tree for child folders
  const childFoldersWithChildren = childFolders.map((childFolder) =>
    buildFolderTreeWithChildren(childFolder.id, allFolders, allFiles)
  );

  return {
    ...folder,
    type: 'FOLDER',
    children: [...childFiles.map((file) => file), ...childFoldersWithChildren],
  };
};

export const selectProjectBankFolderTree = createSelector(
  [
    (state, folderId) => folderId,
    selectProjectFolderFilePermissionsArrayWithJoinedData,
    selectProjectBankFilesFilePermissionsArrayWithJoinedData,
  ],
  (folderId, allFolders, allFiles) => {
    if (!folderId) {
      return { folders: [], files: [], allItems: [] };
    }

    // Get all descendant folders recursively
    const descendantFolders = getDescendantFolders(folderId, allFolders);
    const descendantFolderIds = new Set([
      folderId,
      ...descendantFolders.map((f) => f.id),
    ]);

    // Get all files that belong to any of these folders
    const descendantFiles = allFiles.filter((file) =>
      descendantFolderIds.has(file.parent)
    );

    const tree = buildFolderTreeWithChildren(folderId, allFolders, allFiles);

    return {
      folders: descendantFolders,
      files: descendantFiles,
      allItems: [...descendantFolders, ...descendantFiles],
      tree,
    };
  }
);

/**
 * Get full folder tree (folders and files) for the selected folder
 */
export const selectSelectedProjectBankFolderFullTree = createSelector(
  [selectProjectBankFolderTree],
  (tree) => tree.tree
);

export const selectSelectedFilesByIds = createSelector(
  [selectProjectBankFilesFilePermissions, (_, fileIds) => fileIds],
  (allFiles, fileIds = []) =>
    (fileIds || []).map((id) => allFiles[id]).filter((file) => !!file)
);

export const selectSelectedProjectBankFileObjects = createSelector(
  [selectSelectedProjectBankFiles, selectProjectBankFilesFilePermissionsArray],
  (selectedIds, filePermissions) =>
    filePermissions.filter((fp) => selectedIds.includes(fp.id))
);

export const selectPrivateProjectFoldersNavigationPath = createSelector(
  [
    selectSelectedPrivateProjectBankFolderItem,
    selectPrivateProjectBankItemsEntities,
  ],
  (selectedPrivateProjectBankFolder, projectBankItemEntities) => {
    const pathFoldersIds = selectedPrivateProjectBankFolder
      ? selectedPrivateProjectBankFolder.path.split('/')
      : [];

    const pathFolders = [];

    pathFoldersIds.forEach((folderId) => {
      if (projectBankItemEntities[folderId]) {
        pathFolders.push(projectBankItemEntities[folderId]);
      }
    });

    return pathFolders;
  }
);

export const selectSelectedPrivateProjectBankItemIds = createSelector(
  [selectProjectBankState],
  (state) => state.selectedPrivateProjectBankItemIds
);
